import React, {
  FC, useState,
} from 'react';
import {
  Button,
  Col,
  Row,
  Switch,
  Table,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import { SkeletonLoader, TagFilter } from '..';
import styles from './styles.module.less';
import { useExpandingAllInTable } from '../../../hooks';
import { getColumnSearchProps } from '../../../utils/table';

interface Props {
  isLoading: boolean,
  onTagFilterChange?: (value: string[]) => void,
  tagFilterValue?: string[],
  tagFilterOptions?: string[],
  tagFilterChanges?: Map<string, string[]>,
  expandedRowRender: (record: any) => React.ReactElement,
  showChangesOnly?: boolean,
  onShowChangesOnlyToggle?: (value: boolean) => void,
  dataIndex: string,
  data: any[] | undefined,
  additionalColumns?: ColumnsType<any>,
}

const UpdateToolExpandableTable: FC<Props> = ({
  isLoading,
  onTagFilterChange,
  tagFilterValue,
  tagFilterOptions,
  tagFilterChanges,
  expandedRowRender,
  showChangesOnly,
  onShowChangesOnlyToggle,
  dataIndex,
  data,
  additionalColumns,
}) => {
  const [isSortDirectionsAscend, setSortDirectionsAscend] = useState<boolean>(true);

  const onClickShowChanges = (checked: boolean, e) => {
    e.stopPropagation();
    onShowChangesOnlyToggle?.(checked);
  };
  const allKeys = data?.map(item => item[dataIndex]) || [];
  const expandController = useExpandingAllInTable(allKeys, dataIndex, false);

  const getColumns = (): ColumnsType<any> => [
    {
      title: 'Name',
      dataIndex,
      key: 'label',
      showSorterTooltip: { target: 'sorter-icon' },
      sorter: (a, b) => String(a.label).localeCompare(String(b.label)),
      ...getColumnSearchProps(dataIndex),
    },
    ...(additionalColumns ?? []),
  ];

  return (
    <div>
      <Row gutter={64} justify="space-between">
        <Col style={{ margin: 10 }}>
          {onTagFilterChange && (
            <TagFilter
              options={tagFilterOptions!}
              value={tagFilterValue!}
              changes={tagFilterChanges!}
              onChange={v => onTagFilterChange(v)}
            />
          )}
        </Col>
        <Col>
          {onShowChangesOnlyToggle && (
            <div className={styles.rightContainer}>
              <div>
                <span className={styles.showChanges}>Show changes only</span>
                <Switch
                  size="small"
                  onClick={onClickShowChanges}
                  checked={showChangesOnly}
                />
              </div>
            </div>
          )}
        </Col>
      </Row>
      <Table
        columns={getColumns()}
        pagination={false}
        locale={{
          emptyText: isLoading ? <SkeletonLoader /> : '',
        }}
        expandable={{
          expandedRowRender: record => expandedRowRender(record),
          expandedRowKeys: expandController.expandedRowKeys,
          onExpand: expandController.onExpand,
          columnTitle: <Button
            style={{ backgroundColor: '#f6f6f6', height: 17 }}
            className={`ant-table-row-expand-icon ${expandController.isAllExpanded()
              ? 'ant-table-row-expand-icon-expanded'
              : 'ant-table-row-expand-icon-collapsed'
            }`}
            size="small"
            onClick={() => expandController.toggleAllExpanded()}
          />,
        }}
        showSorterTooltip={false}
        dataSource={data}
        className={expandController.isAllExpanded() ? 'table-expanding-all' : ''}
        rowKey={expandController.rowKey}
        onHeaderRow={() => ({
          onClick: () => { setSortDirectionsAscend(!isSortDirectionsAscend); },
        })}
      />
    </div>
  );
};

export default UpdateToolExpandableTable;
